import { useContext, type Dispatch, type MutableRefObject, type ReactNode, type SetStateAction } from 'react';
import type L from 'leaflet';

import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { ValidationContext } from 'contexts/ValidationContext';
import { DeleteIcon, EditIcon, SaveIcon } from 'components/icons';

type BorderType = 'legalBorder' | 'buildableBorder' | 'analysisArea';

interface DeleteUpdateBorderButtonProps {
  isReview?: boolean;
  setGridParams: (newParams: Record<string, unknown>) => void;
  handleEditButtonClick: (borderType: BorderType) => void;
  borderType: BorderType;
  borderTypeRef: MutableRefObject<L.Polygon | null>;
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  btnText: string;
  // ButtonGroup's color union is stricter than Button's (no custom `neutral`);
  // use it so `color` type-checks on both the group and its child buttons.
  color: ButtonGroupProps['color'];
  enableRotateMode?: (borderType: BorderType) => void;
  enableDragMode?: (borderType: BorderType) => void;
  centerOfAnalysisAreaRef?: MutableRefObject<L.Marker | null>;
  isEditing?: boolean;
  tooltipTitle?: string;
  onDelete?: (borderType: BorderType) => void;
  editButtonDisabled?: boolean;
  editButtonTooltip?: string;
  leadingActionButton?: ReactNode;
  onBeforeAction?: () => void;
  hideLabelChip?: boolean;
  onCustomDelete?: () => void;
}

const DeleteUpdateBorderButton = ({
  isReview = false,
  setGridParams,
  handleEditButtonClick,
  borderType,
  borderTypeRef,
  setShowEditableCoordinatesArea,
  btnText,
  color,
  centerOfAnalysisAreaRef,
  isEditing = false,
  tooltipTitle,
  onDelete,
  editButtonDisabled = false,
  editButtonTooltip,
  leadingActionButton = null,
  onBeforeAction,
  hideLabelChip = false,
  onCustomDelete,
}: DeleteUpdateBorderButtonProps) => {
  const { setIsGridAnalysisAreaValid, setGridAnalysisAreaErrorMessage } = useContext(ValidationContext);
  const isEditDisabled = isReview || editButtonDisabled;

  const editButton = (
    <Button
      size="small"
      color={color}
      disabled={isEditDisabled}
      className={
        `border-action-icon-white ${isEditing && !isEditDisabled ? 'save-button-active' : ''} ${isEditDisabled ? 'border-action-disabled' : ''}`.trim() ||
        undefined
      }
      data-border-action="edit"
      aria-disabled={isEditDisabled}
      tabIndex={isEditDisabled ? -1 : 0}
      onClick={(event) => {
        if (isEditDisabled) {
          event.preventDefault();
          return;
        }
        onBeforeAction?.();
        handleEditButtonClick(borderType);
      }}
    >
      {isEditing ? <SaveIcon style={{ color: '#fff' }} /> : <EditIcon style={{ color: '#fff' }} />}
    </Button>
  );

  const editButtonWithOptionalTooltip =
    isEditDisabled && editButtonTooltip ? (
      <Tooltip title={editButtonTooltip}>
        <span className="border-action-tooltip-wrapper">{editButton}</span>
      </Tooltip>
    ) : (
      editButton
    );

  return (
    <>
      <div className="border-action-group">
        <div className="border-action-controls">
          {!hideLabelChip && <div className={`border-label-chip border-label-chip--${color}`}>{btnText}</div>}
          <ButtonGroup variant="contained" color={color}>
            {leadingActionButton}
            <Button
              size="small"
              color={color}
              disabled={isReview}
              className={`border-action-icon-white ${isReview ? 'border-action-disabled' : ''}`.trim()}
              data-border-action="delete"
              onClick={() => {
                onBeforeAction?.();
                if (onCustomDelete) {
                  onCustomDelete();
                  return;
                }
                const nextParams: Record<string, unknown> = {
                  [borderType]: [],
                  manualCenter: null,
                };
                if (borderType === 'analysisArea') {
                  nextParams.analysisAreaType = '';
                }
                setGridParams(nextParams);
                if (borderTypeRef.current) {
                  borderTypeRef.current.remove();
                  borderTypeRef.current = null;
                }
                if (centerOfAnalysisAreaRef?.current) {
                  centerOfAnalysisAreaRef.current.remove();
                  centerOfAnalysisAreaRef.current = null;
                }
                // setMapCenter([46.8182, 8.2275]);
                // setMapZoom(8);
                setShowEditableCoordinatesArea(false);
                if (borderType === 'analysisArea') {
                  setIsGridAnalysisAreaValid(false);
                  setGridAnalysisAreaErrorMessage('*please define an analysis area');
                }
                onDelete?.(borderType);
              }}
            >
              <DeleteIcon style={{ color: '#fff' }} />
            </Button>
            {editButtonWithOptionalTooltip}
          </ButtonGroup>
        </div>
        {tooltipTitle ? <CustomTooltip title={tooltipTitle} /> : null}
      </div>
    </>
  );
};

export default DeleteUpdateBorderButton;
